import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { PositionLegsTable } from '@/components/positions/PositionLegsTable';
import { ClosePositionForm } from '@/components/positions/ClosePositionForm';
import { PositionAiPanel } from '@/components/positions/PositionAiPanel';
import { ShowFillsToggle } from '@/components/ui/ShowFillsToggle';
import { usePosition } from '@/hooks/queries/usePositions';
import { useChain } from '@/hooks/queries/useChains';
import { useShowFills } from '@/hooks/useShowFills';
import { formatCurrency, formatDate } from '@/lib/formatters';
import { cn } from '@/lib/utils';

export function PositionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: position, isLoading: positionLoading } = usePosition(id || '');
  const [aiOpen, setAiOpen] = useState(false);
  const [showFills, setShowFills] = useShowFills();

  const chainId = position?.tradeChainId;
  const { data: chain, isLoading: chainLoading } = useChain(chainId || '');

  if (!id) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Position not found</p>
        <Link to="/chains" className="text-brand-600 hover:text-brand-700 mt-2 inline-block">
          Back to chains
        </Link>
      </div>
    );
  }

  if (positionLoading || chainLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!position || !chain) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Position not found</p>
        <Link to="/chains" className="text-brand-600 hover:text-brand-700 mt-2 inline-block">
          Back to chains
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Link
        to={`/chains/${chain.id}`}
        className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
      >
        <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to {chain.underlyingSymbol}
      </Link>

      {/* Position Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {chain.underlyingSymbol} Position
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <StatusBadge status={position.status} />
            <span className="text-sm text-gray-500">
              Opened {formatDate(position.openedAt)}
            </span>
            {position.rollNumber > 0 && (
              <span className="text-sm text-gray-500">• Roll #{position.rollNumber}</span>
            )}
          </div>
        </div>
      </div>

      {/* Position Details */}
      <Card>
        <CardHeader className="flex items-center justify-between">
          <CardTitle>Position Legs</CardTitle>
          <ShowFillsToggle showFills={showFills} onChange={setShowFills} />
        </CardHeader>
        <CardContent>
          <PositionLegsTable legs={position.legs} showRemaining showFills={showFills} />
          <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
            <span className="font-medium text-gray-900">Net Premium</span>
            <span
              className={cn(
                'text-lg font-semibold',
                position.netPremium >= 0 ? 'text-green-600' : 'text-red-600'
              )}
            >
              {formatCurrency(position.netPremium)}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* AI Analysis - collapsible */}
      <Card>
        <CardHeader>
          <button
            type="button"
            onClick={() => setAiOpen((v) => !v)}
            className="flex w-full items-center justify-between text-left"
          >
            <CardTitle>AI Analysis</CardTitle>
            <span className="text-sm text-gray-500">{aiOpen ? 'Hide' : 'Show'}</span>
          </button>
        </CardHeader>
        {aiOpen && (
          <CardContent>
            <PositionAiPanel positionId={position.id} key={position.id} />
          </CardContent>
        )}
      </Card>

      {/* Close Form - only show for open positions */}
      {position.status === 'open' && (
        <ClosePositionForm position={position} chainId={chain.id} />
      )}
    </div>
  );
}
